import React, { Component } from 'react';
import { EventBus } from 'src/helpers/new';
import usStates from 'src/helpers/usStates';
import CheckoutContext from '../../../CheckoutContext';
import Next from '../../Next';
import './Billing.scss';
import { CheckoutValidateHelper } from '../CheckoutValidateHelper';
import * as Sentry from '@sentry/react';
import CheckoutHelper from 'src/pages/Checkout/CheckoutHelper';
import { CheckoutAPI } from 'src/helpers/new/ApiService';

export interface IBilling {
    streetLines: string[];
    town: string;
    state: string;
    zipCode: string;
    firstName: string;
    lastName: string;
    startedAt: string;
    updatedAt: string;
    createdAt: string;
}

export interface IUser {
    _id: string;
    email: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    addressLineOne: string;
    addressLineTwo: string;
    townCity: string;
    state: string;
    zipCode: string;
    billingAddress?: {
        streetLines: string[];
        town: string;
        state: string;
        zipCode: string;
        country: string;
        firstName: string;
        lastName: string;
    };
}

export interface IResidential {
    streetLines: string[];
    town: string;
    state: string;
    zipCode: string;
    firstName: string;
    lastName: string;
    startedAt: string;
}

interface IState {
    billing: IBilling;
    residential: IResidential;
    isSameAsResidential: boolean;
    addressVerified: boolean;
    isAddressValid: boolean;
    addressValidationError: string;
    isValidatingAddress: boolean;
    activeForm: 'residential' | 'billing';
    validation: {
        residentialAddressLineMessage: string;
        residentialTownMessage: string;
        residentialZipCodeMessage: string;
        residentialFirstNameMessage: string;
        residentialLastNameMessage: string;
        residentialStateMessage: string;
        addressLineMessage: string;
        townMessage: string;
        zipCodeMessage: string;
        firstNameMessage: string;
        lastNameMessage: string;
        stateMessage: string;
    };
}

export default class Billing extends Component<unknown, IState> {
    static readonly contextType = CheckoutContext;
    context!: React.ContextType<typeof CheckoutContext>;

    constructor(props: unknown, context: any) {
        super(props);
        const shouldDefaultToSameAsResidential = this.shouldDefaultToSameAsResidential(context.user);

        this.state = {
            billing: {
                ...context.billingAddress,
                startedAt: context.billingAddress.startedAt || new Date(),
            },
            residential: {
                streetLines: context.residentialAddress?.streetLines || [''],
                town: context.residentialAddress?.town || '',
                state: context.residentialAddress?.state || '',
                zipCode: context.residentialAddress?.zipCode || '',
                firstName: context.residentialAddress?.firstName || '',
                lastName: context.residentialAddress?.lastName || '',
                startedAt: context.billingAddress.startedAt || new Date(),
            },
            isSameAsResidential: true,
            addressVerified: context.addressVerified || false,
            isAddressValid: context.isAddressValid || false,
            addressValidationError: '',
            isValidatingAddress: false,
            activeForm: shouldDefaultToSameAsResidential ? 'residential' : 'billing',
            validation: {
                residentialAddressLineMessage: '',
                residentialTownMessage: '',
                residentialZipCodeMessage: '',
                residentialFirstNameMessage: '',
                residentialLastNameMessage: '',
                residentialStateMessage: '',
                addressLineMessage: '',
                townMessage: '',
                zipCodeMessage: '',
                firstNameMessage: '',
                lastNameMessage: '',
                stateMessage: '',
            },
        };
    }

    componentDidMount() {
        EventBus.on('validate-billing-tab', this.validateInputs);

        this.context.updateCheckoutState({
            isSameAsResidential: this.state.isSameAsResidential,
        });
    }

    componentWillUnmount() {
        EventBus.remove('validate-billing-tab', this.validateInputs);
    }

    shouldDefaultToSameAsResidential = (user: IUser): boolean => {
        if (!user) return true;

        const hasBillingInfo = !!(
            user.billingAddress?.streetLines?.[0] ||
            user.billingAddress?.town ||
            user.billingAddress?.state ||
            user.billingAddress?.zipCode ||
            user.billingAddress?.firstName ||
            user.billingAddress?.lastName
        );

        return !hasBillingInfo;
    };

    updateAddressFields = <T extends IBilling | IResidential>(
        address: T,
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
        isStreetLine = false,
    ): T => {
        const updatedAddress = { ...address };

        if (isStreetLine) {
            updatedAddress.streetLines[parseInt(e.target.name)] = e.target.value;
        } else {
            const key = e.target.name as Exclude<keyof T, 'streetLines' | 'startedAt' | 'updatedAt' | 'createdAt'>;
            updatedAddress[key] = e.target.value as (typeof updatedAddress)[typeof key];
        }

        return updatedAddress;
    };

    handleInputChange =
        (addressType: 'residential' | 'billing', isStreetLine = false) =>
        (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
            this.setState((prevState: Pick<IState, 'billing' | 'residential' | 'isSameAsResidential'>) => {
                const currentAddress = addressType === 'residential' ? prevState.residential : prevState.billing;
                const updatedAddress = this.updateAddressFields(currentAddress, e, isStreetLine);
                const contextUpdate: Partial<{
                    residentialAddress: IResidential;
                    billingAddress: IBilling;
                    isSameAsResidential: boolean;
                }> = {};

                if (addressType === 'residential') {
                    contextUpdate.residentialAddress = updatedAddress;

                    if (prevState.isSameAsResidential) {
                        const updatedBilling = this.updateAddressFields(prevState.billing, e, isStreetLine);

                        contextUpdate.billingAddress = updatedBilling;

                        this.context.updateCheckoutState(contextUpdate);
                        return {
                            residential: updatedAddress,
                            billing: updatedBilling,
                        };
                    }
                } else {
                    contextUpdate.billingAddress = updatedAddress as IBilling;
                }

                this.context.updateCheckoutState(contextUpdate);

                if (addressType === 'residential') {
                    return {
                        residential: updatedAddress,
                        billing: prevState.billing,
                    };
                }

                return {
                    billing: updatedAddress as IBilling,
                    residential: prevState.residential,
                };
            });
        };

    handleResidentialInputChange = this.handleInputChange('residential', false);
    handleResidentialStreetLineInputChange = this.handleInputChange('residential', true);
    handleBillingInputChange = this.handleInputChange('billing', false);
    handleBillingStreetLineInputChange = this.handleInputChange('billing', true);

    handleCheckBoxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const isChecked = e.target.checked;

        this.setState((prevState) => {
            let billing = { ...prevState.billing };

            if (isChecked) {
                billing = {
                    ...billing,
                    streetLines: [...prevState.residential.streetLines],
                    town: prevState.residential.town,
                    state: prevState.residential.state,
                    zipCode: prevState.residential.zipCode,
                    firstName: prevState.residential.firstName,
                    lastName: prevState.residential.lastName,
                };
            }

            this.context.updateCheckoutState({
                billingAddress: billing,
                isSameAsResidential: isChecked,
            });

            return {
                billing,
                isSameAsResidential: isChecked,
                activeForm: isChecked ? 'residential' : 'billing', // Switch to billing form when unchecked
            };
        });
    };

    handleFormSwitch = (formType: 'residential' | 'billing') => {
        this.setState({ activeForm: formType });
    };

    validateInputs = async () => {
        try {
            this.setState({
                isValidatingAddress: true,
            });
            const residentialValidation = CheckoutValidateHelper.validateResidential({}, this.state.residential);

            let billingValidation = { proceed: true, messages: {} };
            if (!this.state.isSameAsResidential) {
                billingValidation = CheckoutValidateHelper.validateBilling({}, this.state.billing);
            }

            const { isAdminPreview } = this.context;
            const canProceed = residentialValidation.proceed && billingValidation.proceed;

            const shouldShowAlerts = residentialValidation.proceed || billingValidation.proceed;

            if (
                shouldShowAlerts &&
                !this.state.isSameAsResidential &&
                !residentialValidation.proceed &&
                this.state.activeForm === 'billing'
            ) {
                EventBus.dispatch('alert', {
                    type: 'error',
                    title: 'Incomplete Address',
                    message:
                        'Residential address is incomplete. Please ensure all required residential address fields are filled. ',
                });
            } else if (
                shouldShowAlerts &&
                !this.state.isSameAsResidential &&
                !billingValidation.proceed &&
                this.state.activeForm === 'residential'
            ) {
                EventBus.dispatch('alert', {
                    type: 'error',
                    title: 'Incomplete Address',
                    message:
                        'Billing address is incomplete. Please ensure all required billing address fields are filled.',
                });
            }

            if (canProceed || isAdminPreview) {
                let validate = false;
                if (!this.state.addressVerified) {
                    const { response } = await CheckoutAPI.validateAddress(this.state.residential);
                    validate = response ?? false;
                }
                if (validate || this.state.addressVerified) {
                    this.setState({
                        addressVerified: true,
                        addressValidationError: '',
                    });
                    this.context.switchTab(3, true);
                    await this.context.updateCart({
                        residentialAddress: {
                            ...this.state.residential,
                            createdAt: this.context.residentialAddress.createdAt ?? new Date(),
                            updatedAt: new Date(),
                        },
                        billingAddress: {
                            ...this.state.billing,
                            createdAt: this.context.billingAddress.createdAt ?? new Date(),
                            updatedAt: new Date(),
                        },
                        isSameAsResidential: this.state.isSameAsResidential,
                        addressVerified: this.state.addressVerified,
                        currentTab: 3,
                        isAddressValid: validate,
                    });
                    if (!isAdminPreview) {
                        CheckoutHelper.metaTrackEvent('AddPaymentInfo');
                    }
                } else {
                    this.setState({
                        addressVerified: true,
                        addressValidationError: 'Please review your address before proceeding.',
                    });
                }
            } else {
                this.setState({
                    validation: {
                        ...residentialValidation.messages,
                        ...billingValidation.messages,
                    },
                });
            }

            window.scrollTo({
                top: 0,
                behavior: 'smooth',
            });
        } catch (error) {
            Sentry.captureException(error);
        } finally {
            this.setState({
                isValidatingAddress: false,
            });
        }
    };

    validateNames = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (!/[a-zA-Z_-]/.test(event.key)) {
            event.preventDefault();
        }
    };

    getStateValue = (stateValue: string): string => {
        if (!stateValue) {
            return 'none';
        }
        return stateValue;
    };

    renderResidentialForm = () => {
        const { residential, validation, addressVerified, addressValidationError } = this.state;
        const {
            residentialFirstNameMessage,
            residentialLastNameMessage,
            residentialTownMessage,
            residentialZipCodeMessage,
            residentialAddressLineMessage,
            residentialStateMessage,
        } = validation;

        return (
            <div className='residential-address-section'>
                <h1>Residential address</h1>
                <h3>Where do you live?</h3>
                {!this.state.isSameAsResidential && (
                    <button
                        type='button'
                        className='edit-address-link'
                        onClick={() => this.handleFormSwitch('billing')}
                    >
                        Edit billing address
                    </button>
                )}
                {addressVerified && addressValidationError && (
                    <p className='address-validation-message'>{addressValidationError}</p>
                )}
                <div className='checkout-form'>
                    <div>
                        <label htmlFor='rFirstName'>First Name *</label>
                        <input
                            type='text'
                            name='firstName'
                            value={residential.firstName ?? ''}
                            id='rFirstName'
                            onChange={this.handleResidentialInputChange}
                            onKeyDown={this.validateNames}
                        />
                        <p>{residentialFirstNameMessage}</p>
                    </div>
                    <div>
                        <label htmlFor='rLastName'>Last Name *</label>
                        <input
                            type='text'
                            name='lastName'
                            value={residential.lastName ?? ''}
                            id='rLastName'
                            onChange={this.handleResidentialInputChange}
                            onKeyDown={this.validateNames}
                        />
                        <p>{residentialLastNameMessage}</p>
                    </div>
                    <div>
                        <label htmlFor='rAddressLine0'>Address line *</label>
                        <input
                            type='text'
                            id='rAddressLine0'
                            value={residential.streetLines[0] ?? ''}
                            name='0'
                            onChange={this.handleResidentialStreetLineInputChange}
                        />
                        <p>{residentialAddressLineMessage}</p>
                    </div>
                    <div>
                        <label htmlFor='rTown'>City *</label>
                        <input
                            type='text'
                            name='town'
                            value={residential.town ?? ''}
                            id='rTown'
                            onChange={this.handleResidentialInputChange}
                        />
                        <p>{residentialTownMessage}</p>
                    </div>
                    <div>
                        <label htmlFor='rState'>State *</label>
                        <select
                            name='state'
                            id='rState'
                            value={this.getStateValue(residential.state)}
                            onChange={this.handleResidentialInputChange}
                            className={`stateDropddown`}
                        >
                            <option value='none' disabled>
                                Select a State
                            </option>
                            {usStates.map(({ key, value }: { key: string; value: string }) => (
                                <option value={key} key={key}>
                                    {key}
                                </option>
                            ))}
                        </select>
                        <p>{residentialStateMessage}</p>
                    </div>
                    <div>
                        <label htmlFor='rZipCode'>Zip code *</label>
                        <input
                            className='zip-code-input'
                            name='zipCode'
                            value={residential.zipCode ?? ''}
                            id='rZipCode'
                            onChange={this.handleResidentialInputChange}
                            onKeyDown={(event) => {
                                const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'];
                                if (!/\d/.test(event.key) && !allowedKeys.includes(event.key)) {
                                    event.preventDefault();
                                }
                            }}
                        />
                        <p>{residentialZipCodeMessage}</p>
                    </div>
                </div>
            </div>
        );
    };

    renderBillingForm = () => {
        const { billing, validation, addressVerified, addressValidationError } = this.state;
        const { firstNameMessage, lastNameMessage, townMessage, zipCodeMessage, addressLineMessage, stateMessage } =
            validation;

        return (
            <div className='billing-address-section'>
                <h1>Billing address</h1>
                <h3>The address your payment card is registered with.</h3>
                <button
                    type='button'
                    className='edit-address-link'
                    onClick={() => this.handleFormSwitch('residential')}
                >
                    Edit residential address
                </button>
                {addressVerified && addressValidationError && (
                    <p className='address-validation-message'>{addressValidationError}</p>
                )}
                <div className='checkout-form'>
                    <div>
                        <label htmlFor='aFirstName'>First Name *</label>
                        <input
                            type='text'
                            name='firstName'
                            value={billing.firstName ?? ''}
                            id='aFirstName'
                            onChange={this.handleBillingInputChange}
                            onKeyDown={this.validateNames}
                        />
                        <p>{firstNameMessage}</p>
                    </div>
                    <div>
                        <label htmlFor='aLastName'>Last Name *</label>
                        <input
                            type='text'
                            name='lastName'
                            value={billing.lastName ?? ''}
                            id='aLastName'
                            onChange={this.handleBillingInputChange}
                            onKeyDown={this.validateNames}
                        />
                        <p>{lastNameMessage}</p>
                    </div>
                    <div>
                        <label htmlFor='aAddressLine0'>Address line *</label>
                        <input
                            type='text'
                            id='aAddressLine0'
                            value={billing.streetLines[0] ?? ''}
                            name='0'
                            onChange={this.handleBillingStreetLineInputChange}
                        />
                        <p>{addressLineMessage}</p>
                    </div>
                    <div>
                        <label htmlFor='aTown'>City *</label>
                        <input
                            type='text'
                            name='town'
                            value={billing.town ?? ''}
                            id='aTown'
                            onChange={this.handleBillingInputChange}
                        />
                        <p>{townMessage}</p>
                    </div>
                    <div>
                        <label htmlFor='aState'>State *</label>
                        <select
                            name='state'
                            id='aState'
                            value={this.getStateValue(billing.state)}
                            onChange={this.handleBillingInputChange}
                            className={`stateDropddown`}
                        >
                            <option value='none' disabled>
                                Select a State
                            </option>
                            {usStates.map(({ key, value }: { key: string; value: string }) => (
                                <option value={key} key={key}>
                                    {key}
                                </option>
                            ))}
                        </select>
                        <p>{stateMessage}</p>
                    </div>
                    <div>
                        <label htmlFor='aZipCode'>Zip code *</label>
                        <input
                            className='zip-code-input'
                            name='zipCode'
                            value={billing.zipCode ?? ''}
                            id='aZipCode'
                            onChange={this.handleBillingInputChange}
                            onKeyDown={(event) => {
                                const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'];
                                if (!/\d/.test(event.key) && !allowedKeys.includes(event.key)) {
                                    event.preventDefault();
                                }
                            }}
                        />
                        <p>{zipCodeMessage}</p>
                    </div>
                </div>
            </div>
        );
    };

    render() {
        const { isSameAsResidential, isValidatingAddress, activeForm } = this.state;

        return (
            <>
                <div className='checkout-billing'>
                    {isSameAsResidential && this.renderResidentialForm()}

                    {!isSameAsResidential && (
                        <div className={`form-container ${activeForm}-form-active`}>
                            {activeForm === 'residential' ? this.renderResidentialForm() : this.renderBillingForm()}
                        </div>
                    )}
                </div>
                <div className='checkout-billing-footer'>
                    <div className='checkbox-section'>
                        <label htmlFor='same-as-residential' className='check-label'>
                            <input
                                type='checkbox'
                                name='same-as-residential'
                                id='same-as-residential'
                                checked={isSameAsResidential}
                                onChange={this.handleCheckBoxChange}
                            />{' '}
                            My billing address is the same as my residential address
                        </label>
                    </div>
                    <Next onClick={this.validateInputs} disabled={isValidatingAddress} />
                </div>
            </>
        );
    }
}
